/**
 * Area verdict (spec section 5.5). Pure: the page builds it on the server, and the verdict panel
 * re-runs it on the client after an ability filter, from the same rows.
 *
 * Rules: go at 7.5 and above, marginal from 5.5, flat below (Companion's thresholds). A
 * red-flagged row is never named. Surf names an experienced-only spot only with that label and
 * only when it beats the best spot for most surfers by at least 0.5. Piste never names off-piste
 * or touring terrain and offers a gentlest option when the leader is not beginner friendly.
 * No surf window times while the cross-spot week gate is closed. Each kind has at least three
 * sentence shapes, chosen by deterministic data features so areas do not read identically.
 */
import { COMPANION_GO_LATER_SCORE, COMPANION_GO_NOW_SCORE } from "@/lib/companion/decision";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import type { AreaBoard, AreaRow } from "./row-model";
import type { AreaSeasonState } from "./season";
import type { AreaSnowSummary } from "./snow";
import type { AreaWeek, AreaWeekDay } from "./week";
import { intlTimeZoneAt, zonedDate } from "@/lib/time-zone-rules";

export type AreaVerdictKind = "go" | "marginal" | "flat" | "off-season" | "stale" | "no-data";
export type AreaVerdict = Readonly<{
  kind: AreaVerdictKind;
  /** "Best for most surfers: Watergate Bay, 72, light offshore." */
  headline: string;
  /** "Experienced only: Porthleven, 81, offshore." */
  experienced: string | null;
  /** "North coast is working; south coast is flat." */
  sectionLine: string | null;
  /** One or two more sentences, varied by data. */
  detail: string;
  /** Snapshot time. */
  asOfISO: string;
  /** "Updated Wed 1 Oct at 09:00 (UK time)". */
  asOfLabel: string;
  bestForMostSlug: string | null;
  experiencedSlug: string | null;
}>;

export type AreaVerdictInput = Readonly<{
  board: AreaBoard;
  week: AreaWeek | null;
  /** Piste only. */
  season: AreaSeasonState | null;
  /** Piste only. */
  snow: AreaSnowSummary | null;
  now: Date;
}>;

/** Minimum lead an experienced-only spot needs over the best spot for most surfers. */
export const EXPERIENCED_LEAD_10 = 0.5;
/** A gentler resort is offered when it trails the leader by less than this. */
export const GENTLEST_GAP_10 = 1.0;
/** Two sections this far apart get a section line. */
export const SECTION_SPLIT_10 = 1.5;

const AUDIENCE: Readonly<Record<string, string>> = {
  surf: "surfers", bodyboarding: "bodyboarders", wind: "kiters and windsurfers", foil: "foilers",
  "downwind-foil": "downwind foilers", "wing-foil": "wingers", diving: "divers", swimming: "swimmers",
  sup: "paddlers", sailing: "sailors", mtb: "riders", "off-piste": "off-piste skiers", touring: "ski tourers",
  piste: "skiers",
};

const SKI_SPORTS = new Set(["piste", "off-piste", "touring"]);

export function verdictKindForScore(score10: number): "go" | "marginal" | "flat" {
  if (score10 >= COMPANION_GO_NOW_SCORE) return "go";
  if (score10 >= COMPANION_GO_LATER_SCORE) return "marginal";
  return "flat";
}

function score(row: AreaRow): number {
  return scoreOutOf100(row.score10 ?? 0);
}

function hash(text: string): number {
  let value = 0;
  for (const char of text) value = (value * 31 + char.charCodeAt(0)) >>> 0;
  return value;
}

/** "Updated Wed 1 Oct at 09:00 (UK time)" in the area's timezone. */
export function asOfLabelFor(iso: string | null, timezone: string, prefix = "Updated"): string {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "Not yet updated";
  const zone = timezone || "UTC";
  // tzdata 2026e's wall clock even on a runtime whose data predates it (lib/time-zone-rules.ts).
  const shownZone = intlTimeZoneAt(zone, date.getTime());
  const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: shownZone }).format(date).replace(/,/gu, "");
  const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: shownZone }).format(date);
  return `${prefix} ${day} at ${time} (${zone === "Europe/London" ? "UK" : "local"} time)`;
}

function windPhrase(row: AreaRow): string {
  if (row.stats.kind !== "surf") return "";
  const { windRelation, windKmh } = row.stats;
  if (!windRelation) return "";
  const strength = windKmh === null ? "" : windKmh < 12 ? "light " : windKmh >= 30 ? "strong " : "";
  return `, ${strength}${windRelation}`;
}

function snowPhrase(row: AreaRow): string {
  if (row.stats.kind !== "snow" || row.stats.newSnow48hCm === null || row.stats.newSnow48hCm < 1) return "";
  return `, ${Math.round(row.stats.newSnow48hCm)} cm new snow`;
}

function pick<T>(options: readonly T[], index: number): T {
  return options[((index % options.length) + options.length) % options.length];
}

function isRankable(row: AreaRow): boolean {
  return row.status === "ranked" && row.score10 !== null && row.safetyState !== "flagged-red";
}

function dayOfYear(now: Date, timezone: string): number {
  const iso = zonedDate(now.getTime(), timezone || "UTC");
  const date = new Date(`${iso}T12:00:00Z`);
  return Math.floor((date.getTime() - Date.UTC(date.getUTCFullYear(), 0, 1)) / 86400000);
}

/** Day 0 versus day 1 for the named spot: 1 up, -1 down, 0 flat or unknown. */
function trendFor(week: AreaWeek | null, slug: string | null): -1 | 0 | 1 {
  if (!week || !slug) return 0;
  const days = week.spotOutlooks.find((outlook) => outlook.slug === slug)?.days ?? [];
  const today = days[0]?.score10 ?? null;
  const tomorrow = days[1]?.eligible ? days[1].score10 : null;
  if (today === null || tomorrow === null) return 0;
  if (tomorrow - today >= 0.5) return 1;
  if (today - tomorrow >= 0.5) return -1;
  return 0;
}

function sectionLineFor(board: AreaBoard): string | null {
  const scored = board.sections.filter((section) => section.best?.score10 != null);
  if (scored.length < 2) return null;
  const sorted = [...scored].sort((a, b) => (b.best?.score10 ?? 0) - (a.best?.score10 ?? 0));
  const high = sorted[0];
  const low = sorted[sorted.length - 1];
  const gap = (high.best?.score10 ?? 0) - (low.best?.score10 ?? 0);
  if (gap < SECTION_SPLIT_10) return null;
  const highScore = high.best?.score10 ?? 0;
  const lowScore = low.best?.score10 ?? 0;
  if (highScore >= COMPANION_GO_LATER_SCORE && lowScore < COMPANION_GO_LATER_SCORE) {
    return `${high.name} is working; ${lowerFirst(low.name)} is flat.`;
  }
  return `${high.name} is the better bet today; ${lowerFirst(low.name)} is well behind.`;
}

/** Lower-cases a leading common word ("North coast" to "north coast") but keeps proper names. */
function lowerFirst(name: string): string {
  const [first, ...rest] = name.split(" ");
  const common = new Set(["North", "South", "East", "West", "Northern", "Southern", "Eastern", "Western", "Central", "Inner", "Outer", "Upper", "Lower"]);
  if (common.has(first) && rest.length > 0 && /^[a-z]/u.test(rest[0])) return [first.toLowerCase(), ...rest].join(" ");
  return name;
}

function windowSentence(week: AreaWeek | null, slug: string | null): string | null {
  // Window times only when the gate is open (surf stays withheld until certified).
  if (!week?.gate.certified || !week.crossSpot || !slug) return null;
  const today = week.dates[0];
  const window = week.crossSpot.bestWindows.find((candidate) => candidate.slug === slug && candidate.dateISO === today);
  if (!window) return null;
  const hour = (value: number) => `${String(value).padStart(2, "0")}:00`;
  return `The best of it runs from ${hour(window.startHour)} to ${hour(window.endHour)} local time.`;
}

function bestDaySentence(
  week: AreaWeek | null,
  todayScore: number | null,
  experiencedSlugs: ReadonlySet<string>,
  areaName: string,
): string | null {
  const days = week?.crossSpot?.days;
  if (!days || days.length < 2) return null;
  let best: AreaWeekDay | null = null;
  for (const day of days.slice(1)) {
    if (day.best && (!best || day.best.score10 > (best.best?.score10 ?? -Infinity))) best = day;
  }
  if (!best?.best || best.best.score10 < COMPANION_GO_LATER_SCORE) return null;
  if (todayScore !== null && best.best.score10 <= todayScore) return null;
  // An experienced-only spot is never named in the detail, where it would carry no label.
  if (experiencedSlugs.has(best.best.slug)) {
    return `The best day this week in ${areaName} looks like ${best.weekday} (${scoreOutOf100(best.best.score10)}).`;
  }
  return `The best day this week looks like ${best.weekday}, at ${best.best.displayName} (${scoreOutOf100(best.best.score10)}).`;
}

/** `place` is "at {spot}" or "in {area}"; never an experienced-only spot (it would carry no label). */
function tomorrowSentence(trend: -1 | 0 | 1, place: string, kind: AreaVerdict["kind"], variant: number): string {
  if (trend === 1) return pick([`It looks better again tomorrow ${place}.`, `Tomorrow should improve on today.`, `Expect a step up tomorrow.`], variant);
  if (trend === -1) {
    // "Today is the day to go" only fits a day worth going on.
    return kind === "flat"
      ? pick([`Tomorrow looks weaker still.`, `It fades further tomorrow.`, `Tomorrow looks no better.`], variant)
      : pick([`It drops off tomorrow, so today is the day to go.`, `Tomorrow looks weaker, so make the most of today.`, `Conditions fade tomorrow.`], variant);
  }
  return pick([`Tomorrow looks much the same.`, `Little change is expected tomorrow.`, `Expect similar conditions tomorrow.`], variant);
}

function staleVerdict(input: AreaVerdictInput, asOfISO: string, variant: number): AreaVerdict {
  const name = input.board.area.name;
  return {
    kind: "stale",
    headline: pick([
      `Scores for ${name} have not refreshed recently, so there is no best pick to show.`,
      `The latest ${name} scores are out of date, so this list is a guide rather than a call.`,
      `These ${name} rankings come from an earlier update and may have moved since.`,
    ], variant),
    experienced: null,
    sectionLine: null,
    detail: pick([
      "Open a spot for its own latest forecast.",
      "Each spot page shows the newest forecast it holds.",
      "The list returns to live rankings at the next update.",
    ], variant),
    asOfISO,
    asOfLabel: asOfLabelFor(input.board.snapshotUpdatedISO, input.board.area.timezone, "Last updated"),
    bestForMostSlug: null,
    experiencedSlug: null,
  };
}

function offSeasonVerdict(input: AreaVerdictInput, asOfISO: string, asOfLabel: string, variant: number): AreaVerdict {
  const name = input.board.area.name;
  const season = input.season!;
  const opens = season.opensLabel ? ` It ${season.opensLabel}.` : "";
  const glacierNames = season.glacierOpen
    .map((slug) => input.board.rows.find((row) => row.slug === slug)?.displayName ?? null)
    .filter((value): value is string => value !== null);
  const headline = season.state === "pre-season"
    ? pick([
      `The ${name} season has not started yet.${opens}`,
      `${name} is gearing up for winter.${opens}`,
      `Lifts in ${name} are still closed before the season.${opens}`,
    ], variant)
    : pick([
      `${name} is out of season.${opens}`,
      `The lifts in ${name} are closed for the season.${opens}`,
      `It is the off season in ${name}.${opens}`,
    ], variant);
  const detail = glacierNames.length > 0
    ? `Glacier skiing is open at ${glacierNames.join(", ")}.`
    : pick([
      "Check the best months to ski here below.",
      "The season highlights and best months below show when to plan a trip.",
      "Use the best months below to plan your trip.",
    ], variant);
  return {
    kind: "off-season", headline: headline.trim(), experienced: null, sectionLine: null, detail,
    asOfISO, asOfLabel, bestForMostSlug: null, experiencedSlug: null,
  };
}

export function buildAreaVerdict(input: AreaVerdictInput): AreaVerdict {
  const { board, week, now } = input;
  const area = board.area;
  const asOfISO = board.snapshotUpdatedISO ?? now.toISOString();
  const asOfLabel = asOfLabelFor(board.snapshotUpdatedISO, area.timezone);
  const rankable = board.rows.filter(isRankable);
  const ski = SKI_SPORTS.has(board.sport);
  const sectionSplit = sectionLineFor(board) !== null ? 1 : 0;
  const baseVariant = dayOfYear(now, area.timezone) + hash(area.slug) + sectionSplit;

  if (board.snapshotStale) return staleVerdict(input, asOfISO, baseVariant);
  if (ski && input.season && input.season.state !== "in-season" && rankable.length === 0) {
    return offSeasonVerdict(input, asOfISO, asOfLabel, baseVariant);
  }
  if (rankable.length === 0 && board.rows.some((row) => row.status === "stale")) {
    // Rows were ranked, but each one's local day has rolled over since the last refresh: today's
    // scores are on their way, which is not the same as having nothing to rank.
    return {
      kind: "no-data",
      headline: pick([
        `Today's ${area.name} scores are on their way.`,
        `Fresh ${area.name} scores are on their way for today.`,
      ], baseVariant),
      experienced: null,
      sectionLine: null,
      detail: "Scores refresh every three hours; open a spot for its own forecast meanwhile.",
      asOfISO, asOfLabel, bestForMostSlug: null, experiencedSlug: null,
    };
  }
  if (rankable.length === 0) {
    return {
      kind: "no-data",
      headline: pick([
        `No current scores for ${area.name} yet.`,
        `${area.name} has no ranked spots right now.`,
        `There is nothing to rank in ${area.name} at the moment.`,
      ], baseVariant),
      experienced: null,
      sectionLine: null,
      detail: "Scores update several times a day; open a spot for its own forecast.",
      asOfISO, asOfLabel, bestForMostSlug: null, experiencedSlug: null,
    };
  }

  const surf = board.sport === "surf" || board.sport === "bodyboarding";
  const audience = AUDIENCE[board.sport] ?? "most people";
  let leader: AreaRow | null;
  let experienced: AreaRow | null = null;
  if (surf) {
    leader = rankable.find((row) => !row.experiencedOnly) ?? null;
    const expert = rankable.find((row) => row.experiencedOnly) ?? null;
    if (expert && (!leader || (expert.score10 ?? 0) - (leader.score10 ?? 0) >= EXPERIENCED_LEAD_10)) experienced = expert;
  } else {
    leader = rankable[0];
  }
  const headlineRow = leader ?? experienced!;
  const kind = verdictKindForScore(headlineRow.score10 ?? 0);
  const trend = trendFor(week, headlineRow.slug);
  const variant = baseVariant + (trend + 1);
  const n = headlineRow.displayName;
  const s = score(headlineRow);

  let headline: string;
  if (!leader && experienced) {
    headline = pick([
      `No beginner-friendly break is working in ${area.name} today.`,
      `Today in ${area.name} is one for experienced ${audience} only.`,
      `Only the more demanding breaks in ${area.name} are working today.`,
    ], variant);
  } else if (surf) {
    const extra = windPhrase(headlineRow);
    headline = kind === "go"
      ? pick([
        `Best for most ${audience}: ${n}, ${s}${extra}.`,
        `${n} leads ${area.name} for most ${audience} at ${s}${extra}.`,
        `Go to ${n} if you can: ${s}${extra}, the best pick for most ${audience}.`,
      ], variant)
      : kind === "marginal"
        ? pick([
          `Best for most ${audience}: ${n}, ${s}${extra}, worth a look rather than a long drive.`,
          `${n} is the pick of a middling day in ${area.name} at ${s}${extra}.`,
          `Nothing special, but ${n} is the best bet for most ${audience} at ${s}${extra}.`,
        ], variant)
        : pick([
          `Small or messy across ${area.name} today; the best for most ${audience} is ${n} at ${s}.`,
          `A quiet day in ${area.name}: ${n} scores best for most ${audience} at ${s}.`,
          `Not much on in ${area.name} today. ${n} is the least bad for most ${audience} at ${s}.`,
        ], variant);
  } else if (ski) {
    const extra = snowPhrase(headlineRow);
    headline = kind === "go"
      ? pick([
        `Best today: ${n}, ${s}${extra}.`,
        `${n} leads ${area.name} today at ${s}${extra}.`,
        `Head for ${n}: ${s}${extra}, the best in ${area.name} today.`,
      ], variant)
      : kind === "marginal"
        ? pick([
          `Best today: ${n}, ${s}${extra}, decent rather than special.`,
          `${n} edges ${area.name} today at ${s}${extra}.`,
          `A middling day in ${area.name}; ${n} is the pick at ${s}${extra}.`,
        ], variant)
        : pick([
          `A poor day across ${area.name}; ${n} is best at ${s}.`,
          `Conditions are weak in ${area.name} today, with ${n} top at ${s}.`,
          `Not a day to chase in ${area.name}: ${n} leads at only ${s}.`,
        ], variant);
  } else {
    headline = kind === "go"
      ? pick([
        `Best for ${audience}: ${n}, ${s}.`,
        `${n} leads ${area.name} for ${audience} at ${s}.`,
        `Go to ${n} if you can: ${s}, the best in ${area.name} for ${audience}.`,
      ], variant)
      : kind === "marginal"
        ? pick([
          `Best for ${audience}: ${n}, ${s}, fair rather than special.`,
          `${n} is the pick of a middling day for ${audience} in ${area.name} at ${s}.`,
          `Nothing special for ${audience}, but ${n} is the best bet at ${s}.`,
        ], variant)
        : pick([
          `A quiet day for ${audience} across ${area.name}; ${n} is best at ${s}.`,
          `Little on for ${audience} in ${area.name} today, with ${n} top at ${s}.`,
          `Not one to chase: ${n} leads ${area.name} for ${audience} at only ${s}.`,
        ], variant);
  }

  const experiencedLine = experienced
    ? `Experienced only: ${experienced.displayName}, ${score(experienced)}${windPhrase(experienced)}.`
    : null;

  // Detail: one or two sentences from data features.
  const sentences: string[] = [];
  if (ski && leader && board.sport === "piste" && !leader.beginnerFriendly) {
    const gentle = rankable.find((row) => row.beginnerFriendly && (leader.score10 ?? 0) - (row.score10 ?? 0) < GENTLEST_GAP_10);
    if (gentle) sentences.push(`Gentlest option: ${gentle.displayName}, ${score(gentle)}.`);
  }
  const windowLine = windowSentence(week, headlineRow.slug);
  if (windowLine) sentences.push(windowLine);
  const experiencedSlugs = new Set(rankable.filter((row) => row.experiencedOnly).map((row) => row.slug));
  const bestDay = bestDaySentence(week, headlineRow.score10, experiencedSlugs, area.name);
  if (bestDay && sentences.length < 2) sentences.push(bestDay);
  if (ski && input.snow && sentences.length < 2 && input.snow.newSnow24hCm !== null && input.snow.newSnow24hCm >= 5) {
    sentences.push(`Up to ${Math.round(input.snow.newSnow24hCm)} cm fell in the last 24 hours.`);
  }
  if (sentences.length < 2) sentences.push(tomorrowSentence(trend, headlineRow.experiencedOnly ? `in ${area.name}` : `at ${n}`, kind, variant));

  return {
    kind,
    headline,
    experienced: experiencedLine,
    sectionLine: sectionLineFor(board),
    detail: sentences.slice(0, 2).join(" "),
    asOfISO,
    asOfLabel,
    bestForMostSlug: leader?.slug ?? null,
    experiencedSlug: experienced?.slug ?? null,
  };
}
